import { useState, useMemo, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate, Outlet, Navigate } from "react-router-dom";
import { LayoutDashboard, FileText, Settings, HelpCircle, PanelLeft, ChevronUp, Bell, LogOut, Shield, Building2, ShoppingCart, MapPin, ArrowLeftCircle, Inbox, Thermometer, AlertTriangle, Loader2, Puzzle, Plus, RefreshCw, Menu, X, Snowflake, Store, Gift, ClipboardCheck } from "lucide-react";
import ThemeLogo from "@/components/ThemeLogo";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useIsAdmin } from "@/hooks/useUserRole";
import { useUserRole } from "@/hooks/useUserRole";
import { useCompanyInfo } from "@/hooks/useCompanyInfo";
import FeedbackDialog from "@/components/feedback/FeedbackDialog";
import { useNavCounts } from "@/hooks/useNavCounts";
import { usePendingProForma } from "@/hooks/usePendingProForma";
import { useMyPagePermissions } from "@/hooks/useUserPagePermissions";
import { useOnboardingStatus } from "@/hooks/useOnboardingStatus";
import OnboardingDialog from "@/components/onboarding/OnboardingDialog";
import { useIsMobile } from "@/hooks/use-mobile";
import ThemeToggle from "@/components/ThemeToggle";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useLanguage } from "@/contexts/LanguageContext";

interface NavItem {
  nameKey: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

const allNavigation: NavItem[] = [{
  nameKey: "nav.dashboard",
  href: "/dashboard",
  icon: LayoutDashboard
}, {
  nameKey: "nav.orders",
  href: "/dashboard/orders",
  icon: ShoppingCart
}, {
  nameKey: "nav.planning",
  href: "/dashboard/subscriptions",
  icon: RefreshCw
}, {
  nameKey: "nav.invoices",
  href: "/dashboard/invoices",
  icon: FileText
}, {
  nameKey: "nav.inbox",
  href: "/dashboard/inbox",
  icon: Inbox
}, {
  nameKey: "nav.addresses",
  href: "/dashboard/addresses",
  icon: MapPin
}];

const routeTitleKeys: Record<string, string> = {
  "/dashboard": "nav.dashboard",
  "/dashboard/inbox": "nav.inbox",
  "/dashboard/orders": "nav.orders",
  "/dashboard/invoices": "nav.invoices",
  "/dashboard/addresses": "nav.addresses",
  "/dashboard/subscriptions": "nav.planning",
  "/dashboard/settings": "nav.settings",
  "/dashboard/help": "nav.help",
  "/dashboard/new": "nav.newOrder",
};

const infoTickerKeys = [
  { icon: "snowflake" as const, key: "ticker.sublimation" },
  { icon: "thermometer" as const, key: "ticker.temperature" },
  { icon: "shield" as const, key: "ticker.eps" },
  { icon: "warning" as const, key: "ticker.safety" },
];

const InfoTicker = ({ pathname }: { pathname: string }) => {
  const { t } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const showTicker = pathname === "/dashboard/new";

  useEffect(() => {
    if (!showTicker) return;
    const interval = setInterval(() => {
      setIsVisible(false);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % infoTickerKeys.length);
        setIsVisible(true);
      }, 300);
    }, 5000);
    return () => clearInterval(interval);
  }, [showTicker]);

  if (!showTicker) return null;

  const msg = infoTickerKeys[currentIndex];
  const IconComponent = msg.icon === "snowflake" ? Snowflake : msg.icon === "thermometer" ? Thermometer : msg.icon === "warning" ? AlertTriangle : Shield;
  const isWarning = msg.icon === "warning";

  return (
    <div className={cn(
      "mt-3 flex items-center gap-2.5 px-3 py-2 rounded-lg border text-xs transition-all duration-300",
      isWarning
        ? "bg-amber-500/10 border-amber-500/20 text-amber-700 dark:text-amber-300"
        : "bg-primary/5 border-primary/15 text-foreground",
      isVisible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-1"
    )}>
      <div className={cn("w-5 h-5 rounded-md flex items-center justify-center shrink-0", isWarning ? "bg-amber-500/20" : "bg-primary/10")}>
        <IconComponent className={cn("w-3 h-3", isWarning ? "text-amber-600 dark:text-amber-400" : "text-primary")} />
      </div>
      <span className="truncate font-medium">{t(msg.key)}</span>
      <div className="ml-auto flex gap-0.5 shrink-0">
        {infoTickerKeys.map((_, i) => (
          <div key={i} className={cn("w-1 h-1 rounded-full transition-colors", i === currentIndex ? (isWarning ? "bg-amber-500" : "bg-primary") : "bg-muted-foreground/30")} />
        ))}
      </div>
    </div>
  );
};

/* ─── Sidebar Content (shared between desktop sidebar and mobile sheet) ─── */
function SidebarContent({
  navigation,
  navCounts,
  collapsed,
  setCollapsed,
  location,
  userRole,
  isAdmin,
  isAdminLoading,
  impersonation,
  handleStopImpersonation,
  logoUrl,
  companyName,
  userName,
  handleSignOut,
  onNavigate,
  isMobileSheet,
  approvalOnly,
}: {
  navigation: NavItem[];
  navCounts: any;
  collapsed: boolean;
  setCollapsed: (v: boolean) => void;
  location: any;
  userRole: any;
  isAdmin: boolean;
  isAdminLoading: boolean;
  impersonation: any;
  handleStopImpersonation: () => void;
  logoUrl: string | null;
  companyName: string | null;
  userName: string | null;
  handleSignOut: () => void;
  onNavigate?: () => void;
  isMobileSheet?: boolean;
  approvalOnly?: boolean;
}) {
  const { t } = useLanguage();
  return (
    <>
      {/* Frost glow at bottom of sidebar */}
      <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-20 z-10">
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-polar/25 to-transparent animate-frost-breathe" />
      </div>

      {/* Floating ice particles */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden z-0">
        <div className="absolute top-20 left-2 w-2 h-2 rounded-full bg-polar/50 animate-snowflake" style={{ animationDelay: "0s" }} />
        <div className="absolute top-40 right-3 w-1.5 h-1.5 rounded-full bg-arctic/40 animate-snowflake" style={{ animationDelay: "3s" }} />
        <div className="absolute top-60 left-6 w-2.5 h-2.5 rounded-full bg-polar/35 animate-snowflake" style={{ animationDelay: "5.5s" }} />
        {!collapsed && (
          <>
            <div className="absolute top-10 right-8 w-1.5 h-1.5 rounded-full bg-arctic/35 animate-snowflake" style={{ animationDelay: "1.5s" }} />
            <div className="absolute top-80 left-10 w-2 h-2 rounded-full bg-polar/45 animate-snowflake" style={{ animationDelay: "7s" }} />
          </>
        )}
      </div>

      {/* Logo */}
      <div className={cn("h-14 flex items-center border-b border-sidebar-border relative z-20", collapsed ? "justify-center px-1" : "justify-between px-3")}>
        {!collapsed && (
          <div className="flex items-center gap-2 min-w-0">
            {isMobileSheet ? (
              <img src="/favicon.png" alt="Dutch DryIce" className="h-7 w-7" />
            ) : (
              <ThemeLogo className="h-5" variant="light" />
            )}
          </div>
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className={cn(
            "rounded-md flex items-center justify-center text-sidebar-foreground/50 hover:bg-sidebar-accent hover:text-sidebar-foreground transition-colors shrink-0 hidden md:flex",
            collapsed ? "w-10 h-10" : "w-7 h-7"
          )}
        >
          <PanelLeft className="w-4 h-4" />
        </button>
      </div>

      {/* Nieuwe bestelling button */}
      {!approvalOnly && (
      <div className="px-3 pt-4 pb-2 relative z-20">
        <Link
          to="/dashboard/new"
          onClick={onNavigate}
          className={cn(
            "flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-bold transition-all duration-200 bg-primary text-primary-foreground shadow-frost hover:bg-primary/90",
            collapsed && "px-0"
          )}
        >
          <Plus className="w-5 h-5 shrink-0" />
          {!collapsed && <span>{t("nav.newOrder")}</span>}
        </Link>
      </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 px-3 pb-4 space-y-1 relative z-20">
        {navigation.map(item => {
          const isActive = location.pathname === item.href;
          const countMap: Record<string, number> = {
            "/dashboard/inbox": navCounts?.inbox || 0,
            "/dashboard/orders": navCounts?.orders || 0,
            "/dashboard/invoices": navCounts?.invoices || 0,
            "/dashboard/subscriptions": navCounts?.planning || 0,
            "/dashboard/goedkeuren": navCounts?.proForma || 0,
          };
          const badgeCount = countMap[item.href] || 0;
          const showBadge = badgeCount > 0;
          return (
            <Link
              key={item.nameKey}
              to={item.href}
              onClick={onNavigate}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 relative",
                collapsed && "justify-center px-0",
                isActive ? "bg-primary/10 text-primary" : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground"
              )}
            >
              <div className="relative shrink-0">
                <item.icon className="w-5 h-5" />
                {showBadge && collapsed && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 bg-destructive text-destructive-foreground text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-sidebar">
                    {badgeCount > 99 ? "99+" : badgeCount}
                  </span>
                )}
              </div>
              {!collapsed && (
                <span className="flex-1 flex items-center justify-between">
                  {t(item.nameKey)}
                  {showBadge && (
                    <span className={cn(
                      "min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full flex items-center justify-center",
                      isActive ? "bg-primary/20 text-primary" : "bg-destructive text-destructive-foreground"
                    )}>
                      {badgeCount > 99 ? "99+" : badgeCount}
                    </span>
                  )}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Shop */}
      {!approvalOnly && (
      <div className="px-3 pb-1 relative z-20">
        <Link
          to="/dashboard/shop"
          onClick={onNavigate}
          className={cn(
            "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200",
            collapsed && "justify-center px-0",
            location.pathname === "/dashboard/shop"
              ? "bg-sidebar-primary text-sidebar-primary-foreground shadow-frost"
              : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground"
          )}
        >
          <Store className="w-5 h-5 shrink-0" />
          {!collapsed && <span>{t("nav.shop")}</span>}
        </Link>
      </div>
      )}

      {/* Add-ons */}
      {!approvalOnly && (
      <div className="px-3 pb-2 relative z-20">
        <Link
          to="/dashboard/addons"
          onClick={onNavigate}
          className={cn(
            "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200",
            collapsed && "justify-center px-0",
            location.pathname === "/dashboard/addons"
              ? "bg-sidebar-primary text-sidebar-primary-foreground shadow-frost"
              : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground"
          )}
        >
          <Puzzle className="w-5 h-5 shrink-0" />
          {!collapsed && <span>{t("nav.addons")}</span>}
        </Link>
      </div>
      )}


      {/* Admin Link */}
      {!approvalOnly && !isAdminLoading && isAdmin && (
        <div className="px-3 pb-2 relative z-20">
          <Link
            to="/admin"
            onClick={onNavigate}
            className={cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200",
              collapsed && "justify-center px-0",
              location.pathname === "/admin"
                ? "bg-sidebar-primary text-sidebar-primary-foreground shadow-frost"
                : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground"
            )}
          >
            <Shield className="w-5 h-5 shrink-0" />
            {!collapsed && <span>{t("nav.admin")}</span>}
          </Link>
        </div>
      )}

      {/* Impersonation Banner */}
      {impersonation && (
        <div className="px-3 pb-2 relative z-20">
          <button
            onClick={handleStopImpersonation}
            className={cn(
              "w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30 hover:bg-amber-500/25 transition-colors",
              collapsed && "justify-center"
            )}
          >
            <ArrowLeftCircle className="w-4 h-4 shrink-0" />
            {!collapsed && <span>{t("nav.backToAccount")}</span>}
          </button>
        </div>
      )}

      {/* Bottom - Profile */}
      <div className="p-3 border-t border-sidebar-border relative z-20">
        <Popover>
          <PopoverTrigger asChild>
            <button className={cn("w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-sidebar-accent transition-colors", collapsed ? "justify-center" : "")}>
              {logoUrl ? (
                <div className="w-8 h-8 rounded-full bg-sidebar-accent border border-sidebar-border flex items-center justify-center overflow-hidden shrink-0">
                  <img src={logoUrl} alt="Company logo" className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="w-8 h-8 rounded-full bg-sidebar-accent border border-sidebar-border flex items-center justify-center shrink-0">
                  <Building2 className="w-4 h-4 text-sidebar-primary" />
                </div>
              )}
              {!collapsed && (
                <>
                  <div className="flex flex-col min-w-0 text-left flex-1">
                    <span className="text-sm font-semibold text-sidebar-foreground truncate">
                      {companyName || "My Company"}
                    </span>
                    {userName && (
                      <span className="text-xs text-sidebar-foreground/50 truncate">
                        {userName}
                      </span>
                    )}
                  </div>
                  <ChevronUp className="w-4 h-4 text-sidebar-foreground/50 shrink-0" />
                </>
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent side="top" align="start" className="w-56 p-1">
            <Link to="/dashboard/settings" onClick={onNavigate} className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors">
              <Settings className="w-4 h-4 shrink-0" />
              {t("nav.settings")}
            </Link>
            <Link to="/dashboard/referral" onClick={onNavigate} className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors">
              <Gift className="w-4 h-4 shrink-0" />
              Doorverwijzen
            </Link>
            <Link to="/dashboard/help" onClick={onNavigate} className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors">
              <HelpCircle className="w-4 h-4 shrink-0" />
              {t("nav.help")}
            </Link>
            <FeedbackDialog collapsed={false} />
            <div className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground">
              <LanguageSwitcher variant="ghost" size="sm" showLabel />
            </div>
            <div className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground">
              <ThemeToggle size="sm" variant="outline" className="h-8 w-8" />
              <span className="text-sm">{t("nav.theme")}</span>
            </div>
            <div className="my-1 border-t border-border" />
            <button onClick={handleSignOut} className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors">
              <LogOut className="w-4 h-4 shrink-0" />
              {t("nav.logout")}
            </button>
          </PopoverContent>
        </Popover>
      </div>
    </>
  );
}

/* ─── Main Layout ─── */
const DashboardLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const {
    user,
    loading: authLoading,
    signOut,
    impersonation,
    stopImpersonation
  } = useAuth();
  const { isAdmin, isLoading: isAdminLoading } = useIsAdmin();
  const { data: userRole } = useUserRole();
  const { isOnboarded, isLoading: onboardingLoading } = useOnboardingStatus();
  const { logoUrl, companyName, userName } = useCompanyInfo();
  const { data: pagePerms } = useMyPagePermissions();
  const { data: navCounts } = useNavCounts();
  const { data: proForma } = usePendingProForma();

  // Close mobile menu on navigation
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const navigation = useMemo(() => {
    const isInternal = userRole?.role && !["klant", "ontvanger"].includes(userRole.role);
    const pageKeyToHref: Record<string, string> = {
      orders: "/dashboard/orders",
      invoices: "/dashboard/invoices",
    };
    const base = allNavigation.filter(item => {
      if (item.href === "/dashboard" || item.href === "/dashboard/inbox") return true;
      if (isInternal) return true;
      const pageKey = Object.entries(pageKeyToHref).find(([, href]) => href === item.href)?.[0];
      if (!pageKey) return true;
      if (pageKey === "orders" || pageKey === "invoices") return true;
      return pagePerms?.[pageKey] === true;
    });

    // Pro forma landing page logic:
    // - New customer (no historic orders) with an open pro forma → ONLY show the goedkeuren item.
    // - Existing customer with an open pro forma → temporarily prepend the goedkeuren item.
    if (proForma?.hasPending) {
      const approvalItem: NavItem = {
        nameKey: "Bestelling goedkeuren",
        href: "/dashboard/goedkeuren",
        icon: ClipboardCheck,
      };
      if (proForma.isNewCustomer) {
        return [approvalItem];
      }
      return [approvalItem, ...base];
    }
    return base;
  }, [userRole, pagePerms, proForma]);

  useEffect(() => {
    if (!onboardingLoading && !isOnboarded && user) {
      setShowOnboarding(true);
    }
  }, [onboardingLoading, isOnboarded, user]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-glacier flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Accountmanagers werken alleen in de admin-omgeving en hebben geen klantportaal.
  if (userRole?.role === "accountmanager") {
    return <Navigate to="/admin" replace />;
  }

  // New customers with only a pro forma open are locked to the approval page.
  if (
    proForma?.hasPending &&
    proForma.isNewCustomer &&
    location.pathname !== "/dashboard/goedkeuren" &&
    location.pathname !== "/dashboard/settings" &&
    location.pathname !== "/dashboard/help"
  ) {
    return <Navigate to="/dashboard/goedkeuren" replace />;
  }

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };
  const handleStopImpersonation = async () => {
    await stopImpersonation();
    navigate("/admin");
  };

  const sidebarProps = {
    navigation,
    navCounts: { ...(navCounts || {}), proForma: proForma?.count || 0 },
    collapsed,
    setCollapsed,
    location,
    userRole,
    isAdmin: !!isAdmin,
    isAdminLoading,
    impersonation,
    handleStopImpersonation,
    logoUrl: logoUrl || null,
    companyName: companyName || null,
    userName: userName || null,
    handleSignOut,
    approvalOnly: !!(proForma?.hasPending && proForma.isNewCustomer),
  };

  return (
    <>
      <OnboardingDialog open={showOnboarding} onOpenChange={setShowOnboarding} />
      <div className="min-h-screen bg-glacier">
        {/* ─── Mobile Top Header ─── */}
        <header className="md:hidden fixed top-0 left-0 right-0 z-50 h-14 bg-sidebar border-b border-sidebar-border flex items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <Link to="/dashboard">
              <img src="/favicon.png" alt="Dutch DryIce" className="h-7 w-7" />
            </Link>
          </div>
          <div className="flex items-center gap-1.5">
            <Link
              to="/dashboard/inbox"
              className="w-9 h-9 rounded-md flex items-center justify-center text-sidebar-foreground hover:bg-sidebar-accent transition-colors relative"
            >
              <Bell className="w-5 h-5" />
              {(navCounts?.inbox || 0) > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-destructive text-destructive-foreground text-[10px] font-bold rounded-full flex items-center justify-center">
                  {(navCounts?.inbox || 0) > 9 ? "9+" : navCounts?.inbox}
                </span>
              )}
            </Link>
            {!(proForma?.hasPending && proForma.isNewCustomer) && (
              <Link
                to="/dashboard/new"
                className="w-9 h-9 rounded-md bg-primary flex items-center justify-center text-primary-foreground shadow-frost"
              >
                <Plus className="w-5 h-5" />
              </Link>
            )}
            <button onClick={() => setMobileOpen(true)} className="w-9 h-9 rounded-md flex items-center justify-center text-sidebar-foreground hover:bg-sidebar-accent transition-colors">
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </header>

        {/* ─── Mobile Sheet Sidebar ─── */}
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent side="left" className="w-72 p-0 bg-sidebar border-sidebar-border flex flex-col [&>button]:hidden">
            <SidebarContent {...sidebarProps} collapsed={false} onNavigate={() => setMobileOpen(false)} isMobileSheet />
          </SheetContent>
        </Sheet>

        {/* ─── Desktop Sidebar ─── */}
        <aside className={cn(
          "fixed left-0 top-0 bottom-0 z-40 flex-col bg-sidebar border-r border-sidebar-border transition-all duration-300 overflow-hidden hidden md:flex",
          collapsed ? "w-14" : "w-56"
        )}>
          <SidebarContent {...sidebarProps} />
        </aside>

        {/* ─── Main Content ─── */}
        <div className={cn(
          "transition-all duration-300 relative min-h-screen",
          "pt-14 md:pt-0", // top padding for mobile header
          collapsed ? "md:ml-14" : "md:ml-56"
        )}>
          {/* Ambient frost glow */}
          <div className="pointer-events-none fixed top-0 right-0 w-[500px] h-[500px] z-0 hidden md:block">
            <div className="w-full h-full rounded-full bg-polar/15 blur-3xl animate-frost-breathe" />
          </div>

          <main className="px-4 md:px-6 py-4 md:py-6 relative z-10">
            <div className="max-w-5xl mx-auto">
              <InfoTicker pathname={location.pathname} />
            </div>
            <Outlet />
          </main>
        </div>
      </div>
    </>
  );
};
export default DashboardLayout;
